/**
 * References API
 * API для работы со справочниками (clientStatuses, leadSources, countries и т.д.)
 */
import { apiClient, clientDataApi } from '@/api'
import type {
  LeadSource,
  Country,
  CitySearchResult,
  ArchiveReason,
  ClientTagReference,
  ClientFieldDefinition,
} from '../types/references.types'
import type {
  V1LeadSourceInfo,
  V1Country,
  V1CityInfo,
  V1GetLeadSourcesResponse,
  V1GetCountriesResponse,
  V1GetArchiveReasonsResponse,
  V1GetTagsResponse,
  V1GetClientFieldDefinitionsResponse,
} from '@/api/clients/client/models'

// DEV-42: getClientStatuses / mapClientStatus удалены — справочник упразднён.
// DEV-852: getCities (города каталога из properties) удалён — у клиента свой справочник.

/** Маппер LeadSource из API модели в доменную модель */
function mapLeadSource(dto: V1LeadSourceInfo): LeadSource {
  const displayName = dto.displayName || ''
  const description = dto.description || ''

  return {
    id: dto.id || '',
    key: dto.key || '',
    // Backend возвращает только displayName, используем его для всех языков
    nameRu: displayName,
    nameEn: displayName,
    nameVn: displayName,
    descriptionRu: description,
    descriptionEn: description,
    descriptionVn: description,
    sortOrder: 0,
    isActive: true,
  }
}

/** Маппер Country из API модели в доменную модель */
function mapCountry(dto: V1Country): Country {
  return {
    id: dto.id || '',
    name: dto.name || '',
    flag: dto.flag || '',
    isoCode: dto.isoCode || '',
    phoneCode: dto.phoneCode || '',
    localizationKey: dto.localizationKey || '',
    minPhoneLength: dto.minPhoneLength || 0,
    maxPhoneLength: dto.maxPhoneLength || 0,
  }
}

/** Маппер города из поиска client-data в доменную модель (DEV-850) */
function mapCitySearchResult(dto: V1CityInfo): CitySearchResult {
  return {
    id: dto.id || '',
    name: dto.name || '',
    countryId: dto.countryId || '',
    countryName: dto.countryName || '',
    region: dto.region || undefined,
    population: dto.population,
  }
}

export const referencesApi = {
  // DEV-42: getClientStatuses удалён вместе со справочником client_statuses в backend.

  /**
   * Получить список источников лидов
   * Proto: GetLeadSources
   * GET /api/v1/references/lead-sources
   */
  async getLeadSources(): Promise<LeadSource[]> {
    const response = await apiClient.get<V1GetLeadSourcesResponse>(
      '/api/v1/references/lead-sources',
    )
    return (response.data.sources || []).map(mapLeadSource)
  },

  /**
   * Получить справочник причин архивации клиента (DEV-1061).
   * Proto: GetArchiveReasons
   * GET /api/v1/references/archive-reasons
   */
  async getArchiveReasons(): Promise<ArchiveReason[]> {
    const response = await apiClient.get<V1GetArchiveReasonsResponse>(
      '/api/v1/references/archive-reasons',
    )
    return (response.data.reasons || []).map((dto) => ({
      id: dto.id || '',
      key: dto.key || '',
      // Бэкенд отдаёт уже локализованное имя по заголовку User-Locale.
      name: dto.displayName || dto.key || '',
      description: dto.description || undefined,
    }))
  },

  /**
   * Получить справочник тегов клиента (DEV-1062).
   * Proto: GetTags
   * GET /api/v1/references/tags
   */
  async getTags(): Promise<ClientTagReference[]> {
    const response = await apiClient.get<V1GetTagsResponse>('/api/v1/references/tags')
    return (response.data.tags || []).map((dto) => ({
      id: dto.id || '',
      key: dto.key || '',
      name: dto.displayName || dto.key || '',
    }))
  },

  /**
   * Поля карточки клиента, которые этап воронки может требовать заполненными (DEV-2069).
   * Proto: GetClientFieldDefinitions
   * GET /api/v1/references/client-fields
   */
  async getClientFieldDefinitions(): Promise<ClientFieldDefinition[]> {
    const response = await apiClient.get<V1GetClientFieldDefinitionsResponse>(
      '/api/v1/references/client-fields',
    )
    return (response.data.fields || []).map((dto) => ({
      key: dto.key || '',
      name: dto.name || dto.key || '',
      group: dto.group || '',
      groupName: dto.groupName || '',
    }))
  },

  /**
   * Получить список стран
   * Proto: GetCountriesReferences
   * GET /api/v1/references/countries
   */
  async getCountries(): Promise<Country[]> {
    const response = await apiClient.get<V1GetCountriesResponse>('/api/v1/references/countries')
    return (response.data.countries || []).map(mapCountry)
  },

  /**
   * Поиск городов в справочнике client-data (DEV-850)
   * Proto: SearchCities
   * GET /api/v1/references/cities/search
   * Порядок ответа (по населению) сохраняем как есть — бэкенд уже отсортировал.
   */
  async searchCities(query: string, countryId?: string, limit = 10): Promise<CitySearchResult[]> {
    const response = await clientDataApi.clientDataServiceSearchCities(query, countryId, limit)
    return (response.data.cities || []).map(mapCitySearchResult)
  },
}
